class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.measureMent = {
      DEFAULT_STEP: 0.1,
      DEFAULT_MIN_VALUE: 0.5, // Added to avoid undefined errors if used
    };

    // normal handlers debounce
    this.debounceHandleDecreaseQuantity = this.debounce(
      this.handleDecreaseQuantity.bind(this),
      300
    );
    this.debounceHandleIncreaseQuantity = this.debounce(
      this.handleIncreaseQuantity.bind(this),
      300
    );
    this.debounceHandleInputQuantity = this.debounce(
      this.handleInputQuantity.bind(this),
      300
    );

    // unit handlers debounce
    this.debounceHandleIncreaseUnit = this.debounce(
      this.handleIncreaseUnit.bind(this),
      300
    );
    this.debounceHandleDecreaseUnit = this.debounce(
      this.handleDecreaseUnit.bind(this),
      300
    );
    this.debounceHandleInputUnit = this.debounce(
      this.handleInputUnit.bind(this),
      300
    );

    // open / close
    this.debounceHandleOpen = this.debounce(this.handleOpen.bind(this), 300);
    this.debounceHandleClose = this.debounce(this.handleClose.bind(this), 300);
  }

  connectedCallback() {
    this.init();
    this.emptyCartMessage = this.getAttribute("data-empty-cart-message");
  }

  init() {
    this.bindListeners();
  }

  bindListeners() {
    // click
    if (!this.clickHandler) {
      this.clickHandler = (e) => {
        if (e.target.closest("button.cart-drawer__product--minus")) {
          this.debounceHandleDecreaseQuantity(e);
          return;
        }
        if (e.target.closest("button.cart-drawer__product--plus")) {
          this.debounceHandleIncreaseQuantity(e);
          return;
        }
        if (
          e.target.closest("button.cart-drawer__product--minus-measurement")
        ) {
          this.debounceHandleDecreaseUnit(e);
          return;
        }
        if (e.target.closest("button.cart-drawer__product--plus-measurement")) {
          this.debounceHandleIncreaseUnit(e);
          return;
        }
        if (
          e.target.closest(".cart-drawer__content--header--close") ||
          e.target.closest(".cart-drawer__overlay") ||
          e.target.closest(".cart-drawer__close")
        ) {
          this.debounceHandleClose();
          return;
        }
      };
    }

    this.removeEventListener("click", this.clickHandler);
    this.addEventListener("click", this.clickHandler);

    // blur/focusout
    if (!this.focusOutHandler) {
      this.focusOutHandler = (e) => {
        if (e.target.closest("input.cart-drawer__product--normal-input")) {
          this.debounceHandleInputQuantity(e);
          return;
        }
        if (e.target.closest("input.cart-drawer__product--measurement-input")) {
          this.debounceHandleInputUnit(e);
          return;
        }
      };
    }

    this.removeEventListener("focusout", this.focusOutHandler);
    this.addEventListener("focusout", this.focusOutHandler);

    if (!this.docClickHandler) {
      this.docClickHandler = (e) => {
        if (e.target.closest(".slide-menu-cart-new")) {
          e.preventDefault();
          this.debounceHandleOpen();
        }
      };
    }

    document.removeEventListener("click", this.docClickHandler);
    document.addEventListener("click", this.docClickHandler);
  }

  debounce(fn, delay) {
    let timer;
    return function (...args) {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => fn(...args), delay);
    };
  }

  handleOpen() {
    this.classList.remove("cart-drawer--hidden");
    this.classList.add("cart-drawer--open");
    document.documentElement.style.overflow = "hidden";
  }

  handleClose() {
    this.classList.add("cart-drawer--hidden");
    this.classList.remove("cart-drawer--open");
    document.documentElement.style.overflow = "auto";
  }

  handleDecreaseQuantity(e) {
    const minusButton = e.target.closest("button.cart-drawer__product--minus");
    if (!minusButton) {
      console.error("No minus button found");
      return;
    }

    const parentEl = minusButton.closest(".cart-drawer__product--controls");
    if (!parentEl) {
      console.error("No parent element found");
      return;
    }

    const inputEl = parentEl.querySelector(
      ".cart-drawer__product--normal-input"
    );
    if (!inputEl) {
      console.error("No input element found");
      return;
    }

    inputEl.value = Number(inputEl.value) > 1 ? Number(inputEl.value) - 1 : 0;

    this.handleDisableMinusButton(minusButton, Number(inputEl.value));

    const lineItemKey = inputEl.getAttribute("id");
    if (!lineItemKey) {
      console.error("No line item key found");
      return;
    }

    this.handleDisableControls(lineItemKey);
    this.handleQuantityChange(lineItemKey, Number(inputEl.value));
  }

  handleIncreaseQuantity(e) {
    const plusButton = e.target.closest("button.cart-drawer__product--plus");
    if (!plusButton) {
      console.error("No plus button found");
      return;
    }

    const parentEl = plusButton.closest(".cart-drawer__product--controls");
    if (!parentEl) {
      console.error("No parent element found");
      return;
    }

    const inputEl = parentEl.querySelector(
      ".cart-drawer__product--normal-input"
    );
    if (!inputEl) {
      console.error("No input element found");
      return;
    }

    inputEl.value = Number(inputEl.value) + 1;

    const minusButton = parentEl.querySelector(
      "button.cart-drawer__product--minus"
    );
    if (!minusButton) {
      console.error("No minus button found");
      return;
    }

    this.handleDisableMinusButton(minusButton, Number(inputEl.value));

    const lineItemKey = inputEl.getAttribute("id");
    if (!lineItemKey) {
      console.error("No line item key found");
      return;
    }

    this.handleDisableControls(lineItemKey);
    this.handleQuantityChange(lineItemKey, Number(inputEl.value));
  }

  handleInputQuantity(e) {
    const inputEl = e.target.closest(
      "input.cart-drawer__product--normal-input"
    );
    if (!inputEl) {
      console.error("No input element found");
      return;
    }

    const lineItemKey = inputEl.getAttribute("id");
    if (!lineItemKey) {
      console.error("No line item key found");
      return;
    }

    const parentEl = inputEl.closest(".cart-drawer__product--controls");
    if (!parentEl) {
      console.error("No parent element found");
      return;
    }

    if (Number(inputEl.value) <= 0) {
      inputEl.value = 0;
    }

    const minusButton = parentEl.querySelector(
      "button.cart-drawer__product--minus"
    );
    if (!minusButton) {
      console.error("No minus button found");
      return;
    }

    this.handleDisableMinusButton(minusButton, Number(inputEl.value));
    this.handleDisableControls(lineItemKey);
    this.handleQuantityChange(lineItemKey, Number(inputEl.value));
  }

  handleDecreaseUnit(e) {
    const minusButton = e.target.closest(
      ".cart-drawer__product--minus-measurement"
    );
    if (!minusButton) {
      console.error("No minus button found");
      return;
    }

    const parentEl = minusButton.closest(".cart-drawer__product--controls");
    if (!parentEl) {
      console.error("No parent element found");
      return;
    }

    const inputEl = parentEl.querySelector(
      ".cart-drawer__product--measurement-input"
    );
    if (!inputEl) {
      console.error("No input element found");
      return;
    }

    const currentValue = Number.parseFloat(inputEl.value);
    let newValue = this.handleRoundTheValue(
      currentValue - this.measureMent.DEFAULT_STEP
    );

    if (newValue < this.measureMent.DEFAULT_MIN_VALUE) {
      newValue = 0;
    }

    inputEl.value = newValue;

    this.handleDisableMinusButton(minusButton, newValue);

    const lineItemKey = inputEl.getAttribute("id");
    if (!lineItemKey) {
      console.error("No line item key found");
      return;
    }

    this.handleUnitLabel(parentEl, newValue);

    this.handleDisableControls(lineItemKey);

    const quantity = Math.round(newValue / this.measureMent.DEFAULT_STEP);

    this.handleQuantityChange(lineItemKey, quantity,true);
  }

  handleIncreaseUnit(e) {
    const plusButton = e.target.closest(
      ".cart-drawer__product--plus-measurement"
    );
    if (!plusButton) {
      console.error("No plus button found");
      return;
    }

    const parentEl = plusButton.closest(".cart-drawer__product--controls");
    if (!parentEl) {
      console.error("No parent element found");
      return;
    }

    const inputEl = parentEl.querySelector(
      ".cart-drawer__product--measurement-input"
    );
    if (!inputEl) {
      console.error("No input element found");
      return;
    }

    const currentValue = Number.parseFloat(inputEl.value);
    const newValue = this.handleRoundTheValue(
      currentValue + this.measureMent.DEFAULT_STEP
    );

    inputEl.value = newValue;

    const minusButton = parentEl.querySelector(
      "button.cart-drawer__product--plus-measurement"
    );
    if (!minusButton) {
      console.error("No plus button found");
      return;
    }

    this.handleDisableMinusButton(minusButton, newValue);

    const lineItemKey = inputEl.getAttribute("id");
    if (!lineItemKey) {
      console.error("No line item key found");
      return;
    }

    this.handleUnitLabel(parentEl, newValue);
    this.handleDisableControls(lineItemKey);

    const quantity = Math.round(newValue / this.measureMent.DEFAULT_STEP);

    this.handleQuantityChange(lineItemKey, quantity,true);
  }

  handleInputUnit(e) {
    const inputEl = e.target.closest(
      "input.cart-drawer__product--measurement-input"
    );
    if (!inputEl) {
      console.error("No input element found");
      return;
    }

    const parentEl = inputEl.closest(".cart-drawer__product--controls");
    if (!parentEl) {
      console.error("No parent element found");
      return;
    }

    let currentValue = Number.parseFloat(inputEl.value);

    if (
      isNaN(currentValue) ||
      currentValue < this.measureMent.DEFAULT_MIN_VALUE
    ) {
      currentValue = 0;
    }

    const newValue = this.handleRoundTheValue(currentValue);

    inputEl.value = newValue;

    const minusButton = parentEl.querySelector(
      "button.cart-drawer__product--minus-measurement"
    );
    if (!minusButton) {
      console.error("No minus button found");
      return;
    }

    this.handleDisableMinusButton(minusButton, newValue);

    const lineItemKey = inputEl.getAttribute("id");
    if (!lineItemKey) {
      console.error("No line item key found");
      return;
    }

    this.handleUnitLabel(parentEl, newValue);

    this.handleDisableControls(lineItemKey);

    const quantity = Math.round(newValue / this.measureMent.DEFAULT_STEP);

    this.handleQuantityChange(lineItemKey, quantity,true);
  }

  handleUnitLabel(parentEl, value) {
    const unitLabel = parentEl.querySelector(
      ".cart-drawer__product-measurement--label"
    );

    if (!unitLabel) {
      console.error("No unit label found");
      return;
    }

    unitLabel.textContent = unitLabel.textContent.replace(/[\d.]+/, value);
  }

  handleDisableMinusButton(btnElement, currentValue) {
    btnElement.disabled = currentValue <= 0;
  }

  handleRoundTheValue(value) {
    return Math.round(value * 10) / 10;
  }

  handleDisableControls(lineItemKey) {
    const LineItem = this.querySelector(`#cart-item-${lineItemKey}`);
    if (!LineItem) {
      console.error(`No line item found for key: ${lineItemKey}`);
      return;
    }

    const controls = LineItem.querySelector(".cart-drawer__product--controls");
    if (!controls) {
      console.error(`No controls found for line item key: ${lineItemKey}`);
      return;
    }

    controls.classList.add("cart-drawer__product--controls-disabled");
  }

  handleEnableControls(lineItemKey) {
    const LineItem = this.querySelector(`#cart-item-${lineItemKey}`);
    if (!LineItem) {
      console.error(`No line item found for key: ${lineItemKey}`);
      return;
    }

    const controls = LineItem.querySelector(".cart-drawer__product--controls");
    if (!controls) {
      console.error(`No controls found for line item key: ${lineItemKey}`);
      return;
    }

    controls.classList.remove("cart-drawer__product--controls-disabled");
  }

  handleRerender(html, lineItemKey, itemCount) {
    const selectors = [
      "item",
      "discount",
      "original-price",
      "total-price",
      "checkout",
      "additional-checkout",
    ];

    let lineItemRemoved = false;

    selectors.forEach((selector) => {
      if (selector === "item") {
        const newLineItemInfo = html.querySelector(
          `#cart-${selector}-quantity-${lineItemKey}`
        );
        const currentLineItemInfo = this.querySelector(
          `#cart-${selector}-quantity-${lineItemKey}`
        );

        const newLineItemDiscount = html.querySelector(
          `#cart-${selector}-discounts-${lineItemKey}`
        );

        const currentLineItemDiscount = this.querySelector(
          `#cart-${selector}-discounts-${lineItemKey}`
        );


        if (!currentLineItemInfo || !currentLineItemDiscount) {
          console.error(
            `No current line item info found for key: ${lineItemKey} during rerender`
          );
          return;
        }

        if (!newLineItemInfo || !newLineItemDiscount) {
          const lineItem = this.querySelector(`#cart-item-${lineItemKey}`);
          if (!lineItem) {
            return;
          }
          lineItem.remove();
          lineItemRemoved = true;
          return;
        }

        currentLineItemInfo.innerHTML = newLineItemInfo.innerHTML;
        currentLineItemDiscount.innerHTML = newLineItemDiscount.innerHTML;
      } else {
        const newElement = html.querySelector(`#cart-${selector}`);
        const currentElement = this.querySelector(`#cart-${selector}`);

        if (!currentElement) {
          console.error(
            `No current element found for selector: cart-${selector} during rerender`
          );
          return;
        }

        if (!newElement) {
          currentElement.remove();
          return;
        }

        currentElement.innerHTML = newElement.innerHTML;
      }
    });

    if (itemCount !== 0 && !lineItemRemoved)
      this.handleEnableControls(lineItemKey);

    if (itemCount === 0) this.handleEmptyCartState();

    if (itemCount >= 0) this.handleCartCountBubble(itemCount);

    this.init(); // optional re-init, if needed
  }

  handleBodyReRender(newBody) {
    const formBodyCurrent = this.querySelector(
      "form.cart-drawer__content--body"
    );

    if (!formBodyCurrent) {
      console.error("No form body found");
      return;
    }

    formBodyCurrent.innerHTML = newBody;

    this.init();
  }

  handleEmptyCartState() {
    const cartItemsEl = this.querySelector("#cart-items");

    if (!cartItemsEl) {
      console.error(`No #cart-items element found`);
      return;
    }
    const html = `<h6 class="cart-drawer__content__line-items--empty-message">${
      this.emptyCartMessage || "Your basket is empty"
    }</h6>`;

    cartItemsEl.insertAdjacentHTML("afterbegin", html);
  }

  handleCartCountBubble(count) {
   const cartCountElements = document.querySelectorAll(
      "span.cart-item-count-header.cart-item-count-header--quantity"
    );

    // bubble
    const cartCountBubbles = document.querySelectorAll(
      "span.cart-item-count-header.cart-item-count-header--dot"
    );

    if (cartCountElements.length === 0 && cartCountBubbles.length === 0) {
      console.error("No cart count elements found or bubbles found");
      return;
    }

    if (cartCountElements.length) {
      cartCountElements.forEach((element) => {
        element.textContent = count;
      });
    }

    if (cartCountBubbles.length) {
      cartCountBubbles.forEach((element) => {
        if (count > 0) {
          element.classList.remove("cart-item-count-header--dot-hidden");
        } else {
          element.classList.add("cart-item-count-header--dot-hidden");
        }
      });
    }
  }

  handleShowErrorMessage(lineItemKey, message) {
    const lineItem = this.querySelector(`#cart-item-${lineItemKey}`);
    if (!lineItem) {
      console.error(`No line item found to show error for key: ${lineItemKey}`);
      return;
    }

    const inputType = lineItem.getAttribute("data-input-type");

    const error = lineItem.querySelector(".cart-drawer__error");
    if (!error) {
      console.error(`No error element found for line item key: ${lineItemKey}`);
      return;
    }

    const errorMessage = error.querySelector(".cart-drawer__error-message");
    if (!errorMessage) {
      console.error(
        `No error message element found for line item key: ${lineItemKey}`
      );
      return;
    }

    // if (inputType == "unit") {
    //   errorMessage.textContent = this.handleUnitErrorMessage(message);
    //   error.classList.remove("cart-drawer__error--hidden");
    //   return;
    // }

    errorMessage.textContent = message;
    error.classList.remove("cart-drawer__error--hidden");
  }

  handleUnitErrorMessage(message) {
    const numberMatch = message.match(/\d+/);
    if (!numberMatch) return message;

    const originalNumberStr = numberMatch[0];
    // Insert commas in the originalNumber string for thousands
    const originalNumber = parseInt(originalNumberStr, 10);
    const convertedNumber = originalNumber / 10;

    return message.replace(originalNumberStr, convertedNumber.toString());
  }

  handleHideErrorMessage(lineItemKey) {
    const lineItem = this.querySelector(`#cart-item-${lineItemKey}`);
    if (!lineItem) {
      return;
    }

    const error = lineItem.querySelector(".cart-drawer__error");
    if (!error) {
      console.error(`No error element found for line item key: ${lineItemKey}`);
      return;
    }

    error.classList.add("cart-drawer__error--hidden");
  }

  async handleQuantityChange(lineItemKey, currentQuantity = 0,fromUnit = false) {
    try {
      const response = await fetch(
        `${window.Shopify.routes.root}cart/change.js`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            id: lineItemKey,
            quantity: currentQuantity,
            sections: "cart-drawer-new",
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        console.error(
          `API error for line item ${lineItemKey}:`,
          data?.description || data?.message
        );
        throw new Error(data?.description || data?.message);
      }

      if (!data.sections["cart-drawer-new"]) {
        console.error("No cart-drawer-new section found in API response");
        return;
      }

      this.handleRerender(
        new DOMParser().parseFromString(
          data.sections["cart-drawer-new"],
          "text/html"
        ),
        lineItemKey,
        data?.item_count
      );

      this.handleHideErrorMessage(lineItemKey);
    } catch (error) {
      this.handleEnableControls(lineItemKey);
      this.handleShowErrorMessage(
        lineItemKey,
        error?.message || error?.description
      );
      console.error("Error changing quantity:", error);
    }
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.clickHandler);
    this.removeEventListener("focusout", this.focusOutHandler);
    document.removeEventListener("click", this.docClickHandler);
  }
}

customElements.define("cart-drawer", CartDrawer);
